import type { BffSubmitResponse } from '@/services/rivaas-api';
import type { PalmCaptureRejectionReason, PalmHand } from '@repo/common';

import { Step } from '@repo/common';
import { useState } from 'react';

import { setSubmitErrorContextAction, setWorkflowAction } from '@/providers/app-provider/actions';
import { useAppContext } from '@/providers/app-provider/context';
import { selectStep } from '@/providers/app-provider/selectors';
import { postSubmitHandled } from '@/services/rivaas-api';
import { mapBffErrorCodeToFailReason } from '@/utils/bff-error-mapping';
import { nextWorkflowFromSubmit } from '@/utils/step-workflow';

export const PalmUiStep = {
  CAPTURE: 'CAPTURE',
  PROCESSING: 'PROCESSING',
} as const;

export type PalmUiStepValue = (typeof PalmUiStep)[keyof typeof PalmUiStep];

/** BFF `nextStep.data.palmCapture` payload — the other-palm instruction for `both_palms_capture`. */
type PalmCaptureData = {
  capturedHand?: PalmHand;
  expectedHand?: PalmHand;
  phase?: string;
};

export type PalmRejection = {
  message: string;
  reason?: PalmCaptureRejectionReason;
};

/**
 * SAIDVU-499/501 — drives the `both_palms_capture` / `single_palm_capture` submit loop.
 *
 * The BFF resolves the current step server-side, so we just POST the captured palm JPEG and
 * interpret the response (contract: `apps/bff/.../step-handlers/actions/palm-capture.ts`):
 *  - retryable error (`PALM_CAPTURE_REJECTED`)   → show the reason, stay, consume one retake;
 *  - non-retryable error (`PALM_REQUIRED_NOT_CAPTURED`) → advance to the fail screen;
 *  - success + `nextStep.data.palmCapture`       → first palm accepted, prompt the OTHER hand;
 *  - success without that payload                → step complete, advance to the next step.
 */
export function useUploadPalm() {
  const { dispatch, state } = useAppContext();
  const step = selectStep(state);

  const [uiStep, setUiStep] = useState<PalmUiStepValue>(PalmUiStep.CAPTURE);
  const [rejection, setRejection] = useState<PalmRejection | null>(null);
  const [attempt, setAttempt] = useState(0);

  const palmData = step?.data?.palmCapture as PalmCaptureData | undefined;
  const capturedHand = palmData?.capturedHand;
  const expectedHand = palmData?.expectedHand;

  const redirect = {
    unverifiedUrl: state.customerData?.redirect?.unverifiedUrl,
    verifiedUrl: state.customerData?.redirect?.verifiedUrl,
  };

  function advance(response: BffSubmitResponse) {
    const workflow = nextWorkflowFromSubmit(response, redirect);

    if (!workflow) {
      return;
    }

    if (workflow.nextStep.value === Step.FAIL) {
      const failReason = mapBffErrorCodeToFailReason(response.error?.code);

      dispatch(setWorkflowAction({ ...workflow, nextStep: { ...workflow.nextStep, failReason } }));

      return;
    }

    dispatch(setWorkflowAction(workflow));
  }

  async function submitPalm(image: Blob) {
    setRejection(null);
    dispatch(setSubmitErrorContextAction(null));
    setUiStep(PalmUiStep.PROCESSING);

    // DIS `POST /palms` takes the raw palm JPEG (BFF base64-encodes it) — post the image, not the
    // DOT octet-stream liveness record (unlike face; the palm BFF pipeline is image-based).
    const jpeg = image.type === 'image/jpeg' ? image : new Blob([image], { type: 'image/jpeg' });
    const response = await postSubmitHandled(jpeg);

    if (response.error) {
      if (response.error.retryable) {
        setAttempt((count) => count + 1);
        setRejection({
          message: response.error.message,
          reason: response.error.details?.reason as PalmCaptureRejectionReason | undefined,
        });
        setUiStep(PalmUiStep.CAPTURE);

        return;
      }

      advance(response);

      return;
    }

    // First palm accepted (both mode): stay on the step, camera remounts for the other hand.
    if (response.nextStep.data?.palmCapture) {
      setUiStep(PalmUiStep.CAPTURE);
      advance(response);

      return;
    }

    advance(response);
  }

  return { attempt, capturedHand, expectedHand, rejection, submitPalm, uiStep };
}
